import {
	type Icon,
	IconActivity,
	IconAlignLeft,
	IconDots,
	IconDownload,
	IconFileUpload,
	IconGitBranch,
	IconLibrary,
	IconLink,
	IconList,
	IconNote,
	IconPaperclip,
	IconPlus,
	IconRefresh,
	IconRocket,
	IconSitemap,
	IconTrash,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert, Button, Flex, Tabs, Tag, Typography } from "antd";
import { formatDistanceToNow } from "date-fns";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useGitHubAvailable } from "@/common/artemis-operations/apps";
import { useDeleteAssets } from "@/common/artemis-operations/assets";
import { useEnvironments } from "@/common/artemis-operations/environments";
import {
	useProjectRepositories,
	useUnlinkProjectRepository,
} from "@/common/artemis-operations/project-repositories";
import {
	useDeleteProject,
	useProject,
	useUpdateProject,
} from "@/common/artemis-operations/projects";
import {
	useSignalStatuses,
	useSignals,
} from "@/common/artemis-operations/signals";
import type {
	ResourceLinkInput,
	UpdateProjectInput,
} from "@/common/artemis-sdk/graphql";
import { confirmDestructive } from "@/common/confirm";
import { downloadObjectAsMarkdown, slugify } from "@/common/markdown";
import {
	DELETE_PROJECT_DESC,
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	DELETE_RESOURCE_TITLE,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import {
	type Asset,
	type Deployment,
	EnvironmentClassification,
	isHealthSignal,
	ParentType,
	type ProjectRepository,
	ProjectType,
} from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { toLabel } from "@/common/utils";
import { AssetTable } from "@/components/assets/asset-table";
import { AssetViewerDrawer } from "@/components/assets/asset-viewer-drawer";
import { UploadAssetDialog } from "@/components/assets/upload-asset-dialog";
import {
	BaseContainer,
	BasePageContainer,
	CustomEmpty,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { CardBody, CardViewer } from "@/components/card-viewer";
import { DeploymentCreateWizard } from "@/components/deployment/deployment-create-wizard";
import { DocumentEditorDrawer } from "@/components/document-editor-drawer";
import { EnvironmentClassificationBadge } from "@/components/environments/environment-badges";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { AddLinkModal } from "@/components/projects/add-link-modal";
import { DeploymentViewerDrawer } from "@/components/projects/deployment-viewer-drawer";
import { ProjectCatalogGraph } from "@/components/projects/project-catalog-graph";
import {
	NoteDrawer,
	ProjectDetails,
	ProjectLinks,
	ProjectNotesGrid,
} from "@/components/projects/project-details";
import {
	ProjectRepositoryBrowser,
	ProjectRepositoryViewerDrawer,
} from "@/components/projects/project-repository-browser";
import { ReportTable } from "@/components/reports/report-table";
import { SignalCreateWizard } from "@/components/signals/signal-create-wizard";
import { SignalKeyNumbers } from "@/components/signals/signal-key-numbers";
import { SignalList, SignalSummary } from "@/components/signals/signal-list";
import { SignalViewerDrawer } from "@/components/signals/signal-viewer-drawer";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/projects/$projectId",
)({
	component: ProjectPage,
	// `?deployment=<id>` opens that deployment's drawer — deployments have no page of their own.
	validateSearch: (
		search: Record<string, unknown>,
	): { deployment?: string } => ({
		deployment:
			typeof search.deployment === "string" ? search.deployment : undefined,
	}),
});

/** Tab label with a trailing count when non-empty. */
const tabLabel = (name: string, count: number) =>
	count > 0 ? `${name} (${count})` : name;

/** Tab label with a leading icon and an optional trailing count. */
const tabLabelWithIcon = (
	icon: React.ComponentType<React.ComponentPropsWithoutRef<Icon>>,
	name: string,
	count?: number,
) => (
	<Flex align="center" gap={6}>
		<CustomIcon icon={icon} size={16} />
		{count === undefined ? name : tabLabel(name, count)}
	</Flex>
);

function ProjectPage() {
	const { orgSlug, projectId, workspaceId } = Route.useParams();
	const { deployment: linkedDeploymentId } = Route.useSearch();
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { openTaskCreateWizard } = useWizards();
	const { data: environments } = useEnvironments(workspaceId);
	const { data: project, isPending, isError, refetch } = useProject(projectId);
	const { mutate: deleteAssets } = useDeleteAssets();
	const updateProject = useUpdateProject(projectId);
	const deleteProject = useDeleteProject(projectId);
	const [addLinkOpen, setAddLinkOpen] = useState(false);
	const [addNoteOpen, setAddNoteOpen] = useState(false);
	const [instructionsOpen, setInstructionsOpen] = useState(false);
	const [uploadAssetOpen, setUploadAssetOpen] = useState(false);
	const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
	const [deploymentViewerOpen, setDeploymentViewerOpen] = useState(false);
	const [selectedDeployment, setSelectedDeployment] =
		useState<Deployment | null>(null);
	const [deploymentCreateOpen, setDeploymentCreateOpen] = useState(false);
	const [repositoryBrowserOpen, setRepositoryBrowserOpen] = useState(false);
	const [selectedRepository, setSelectedRepository] =
		useState<ProjectRepository | null>(null);
	const { connection: gitProviderConnection, available: gitHubAvailable } =
		useGitHubAvailable(workspaceId);
	const {
		data: projectRepositoriesData,
		isPending: projectRepositoriesPending,
		isError: projectRepositoriesError,
	} = useProjectRepositories(projectId);
	const unlinkProjectRepository = useUnlinkProjectRepository(projectId);
	const { data: signalsData } = useSignals(projectId);
	const signalStatuses = useSignalStatuses(projectId);
	const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
	// Open with an optional deployment to pre-select, e.g. from that deployment's drawer.
	const [signalCreate, setSignalCreate] = useState<{
		open: boolean;
		deploymentId?: string;
	}>({ open: false });

	// Keep the open deployment drawer in sync with refetched project data, so background
	// discovery status (deployment.graph.isDiscovering) propagates into the drawer.
	useEffect(() => {
		setSelectedDeployment((cur) =>
			cur ? (project?.deployments?.find((d) => d?.id === cur.id) ?? cur) : cur,
		);
	}, [project]);

	// Open a deep-linked deployment once the project has loaded, then drop the param.
	useEffect(() => {
		const linked = project?.deployments?.find(
			(d) => d?.id === linkedDeploymentId,
		);
		if (!linked) return;
		setSelectedDeployment(linked);
		setDeploymentViewerOpen(true);
		navigate({ to: ".", search: {}, replace: true });
	}, [project, linkedDeploymentId, navigate]);

	// While any deployment is discovering, poll the project so the deployment cards' "Discovering"
	// status stays live and flips to "Discovered" once the background run finishes.
	const anyDiscovering = (project?.deployments ?? []).some(
		(d) => d?.graph?.isDiscovering,
	);
	useEffect(() => {
		if (!anyDiscovering) return;
		const timer = setInterval(() => refetch(), 4000);
		return () => clearInterval(timer);
	}, [anyDiscovering, refetch]);
	const notify = useNotify();
	const queryClient = useQueryClient();
	const { confirm, dialog } = useFrictionConfirm();

	// Discovery registers the alarms it finds as signals; reload them once a run finishes.
	const wasDiscovering = useRef(false);
	useEffect(() => {
		if (wasDiscovering.current && !anyDiscovering) {
			queryClient.invalidateQueries({ queryKey: ["signals", projectId] });
			queryClient.invalidateQueries({
				queryKey: ["signalStatuses", projectId],
			});
		}
		wasDiscovering.current = anyDiscovering;
	}, [anyDiscovering, projectId, queryClient]);

	const save = (fields: UpdateProjectInput) =>
		updateProject(
			{ ...fields },
			{
				onSuccess: () => notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
				onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
			},
		);

	const handleAddLink = (link: ResourceLinkInput) => {
		save({
			links: [
				...(project?.links ?? []).map((l) => ({
					url: l.url,
					name: l.name,
					description: l.description,
					type: l.type,
				})),
				link,
			],
		});
	};

	const handleAddNote = (note: string) => {
		save({ notes: [...(project?.notes ?? []), note] });
	};

	const downloadRef = useRef(() => {});
	downloadRef.current = () => {
		if (!project) return;
		const notes = (project.notes ?? []).filter(Boolean);
		const body = [
			project.description && `## Description\n\n${project.description}`,
			project.instructions && `## Instructions\n\n${project.instructions}`,
			notes.length
				? `## Notes\n\n${notes.map((n) => `- ${n}`).join("\n")}`
				: "",
		]
			.filter(Boolean)
			.join("\n\n");
		downloadObjectAsMarkdown(
			project as unknown as Record<string, unknown>,
			slugify(project.name),
			{
				title: project.name,
				body,
				exclude: [
					"__typename",
					"assets",
					"reports",
					"deployments",
					"links",
					"notes",
					"description",
					"instructions",
				],
			},
		);
	};

	const confirmDeleteRef = useRef(() => {});
	confirmDeleteRef.current = () => {
		confirm({
			title: DELETE_RESOURCE_TITLE,
			message: DELETE_PROJECT_DESC,
			onConfirm: () => {
				deleteProject(undefined, {
					onSuccess: () => {
						notify.success(DELETE_RESOURCE_INFO_COMPLETED);
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/projects",
							params: { orgSlug, workspaceId },
						});
					},
					onError: (err) =>
						notify.error(
							(err as Error).message || DELETE_RESOURCE_ERROR_FAILED,
						),
				});
			},
		});
	};

	useEffect(() => {
		setTitle(project?.name ?? "Project");
	}, [setTitle, project]);

	useEffect(() => {
		setActions([
			{
				type: "button",
				id: "instructions",
				tooltip: "Agent instructions",
				icon: <CustomIcon icon={IconAlignLeft} />,
				onClick: () => setInstructionsOpen(true),
			},
			{
				type: "submenu",
				id: "add",
				icon: <CustomIcon icon={IconPlus} />,
				submenu: [
					{
						type: "button",
						id: "add-task",
						label: "Task",
						tooltip: "Create a task in this project",
						icon: <CustomIcon icon={IconList} />,
						onClick: () =>
							openTaskCreateWizard({ name: "", description: "", projectId }),
					},
					{
						type: "button",
						id: "add-file",
						label: "File",
						icon: <CustomIcon icon={IconFileUpload} />,
						onClick: () => setUploadAssetOpen(true),
					},
					{
						type: "button",
						id: "add-note",
						label: "Note",
						icon: <CustomIcon icon={IconNote} />,
						onClick: () => setAddNoteOpen(true),
					},
					{
						type: "button",
						id: "add-link",
						label: "Link",
						icon: <CustomIcon icon={IconLink} />,
						onClick: () => setAddLinkOpen(true),
					},
					{
						type: "button",
						id: "add-signal",
						label: "Signal",
						tooltip: "Watch an alarm from this project",
						icon: <CustomIcon icon={IconActivity} />,
						onClick: () => setSignalCreate({ open: true }),
					},
					...(gitHubAvailable && gitProviderConnection
						? [
								{
									type: "button" as const,
									id: "add-repository",
									label: "Repository",
									tooltip: "Link a repository to this project",
									icon: <CustomIcon icon={IconGitBranch} />,
									onClick: () => setRepositoryBrowserOpen(true),
								},
							]
						: []),
					...(project?.type === ProjectType.SOFTWARE_APPLICATION
						? [
								{
									type: "button" as const,
									id: "add-deployment",
									label: "Deployment",
									icon: <CustomIcon icon={IconRocket} />,
									onClick: () => setDeploymentCreateOpen(true),
								},
							]
						: []),
				],
			},

			{
				type: "submenu",
				id: "settings",
				icon: <CustomIcon icon={IconDots} />,
				submenu: [
					{
						type: "button",
						id: "download",
						tooltip: "Download as Markdown",
						label: "Export",
						icon: <CustomIcon icon={IconDownload} />,
						onClick: () => downloadRef.current(),
					},
					{
						type: "button",
						id: "refresh",
						label: "Refresh",
						tooltip: "Refresh",
						icon: <CustomIcon icon={IconRefresh} />,
						onClick: () => refetch(),
					},
					{
						type: "button",
						id: "delete",
						label: "Delete",
						tooltip: "Delete project",
						danger: true,
						icon: <CustomIcon icon={IconTrash} />,
						onClick: () => confirmDeleteRef.current(),
					},
				],
			},
		]);
		return () => setActions([]);
	}, [
		setActions,
		refetch,
		project?.type,
		openTaskCreateWizard,
		projectId,
		gitProviderConnection,
		gitHubAvailable,
	]);

	const reports = project?.reports ?? [];
	const notes = project?.notes ?? [];
	const assets = project?.assets ?? [];
	const links = project?.links ?? [];
	const deployments = project?.deployments ?? [];
	const projectRepositories = projectRepositoriesData ?? [];
	const signals = signalsData ?? [];
	const selectedSignal = signals.find((s) => s.id === selectedSignalId) ?? null;

	const tabItems = [
		{
			key: "signals",
			label: tabLabelWithIcon(IconActivity, "Signals", signals.length),
			children: (
				<SignalList
					signals={signals}
					statuses={signalStatuses.data}
					isFetching={signalStatuses.isFetching}
					deployments={deployments}
					onOpen={(s) => setSelectedSignalId(s.id)}
					onAdd={() => setSignalCreate({ open: true })}
				/>
			),
		},
		{
			key: "reports",
			label: tabLabelWithIcon(IconLibrary, "Reports", reports.length),
			children:
				reports.length > 0 ? (
					<ReportTable
						data={reports}
						// Already scoped to this project — the workspace filter bar is noise here.
						filterBar={undefined}
					/>
				) : (
					<CustomEmpty description="Reports" />
				),
		},
		{
			key: "notes",
			label: tabLabelWithIcon(IconNote, "Notes", notes.length),
			children:
				notes.length > 0 ? (
					<ProjectNotesGrid project={project!} />
				) : (
					<CustomEmpty description="Notes" onAdd={() => setAddNoteOpen(true)} />
				),
		},
		{
			key: "assets",
			label: tabLabelWithIcon(IconPaperclip, "Assets", assets.length),
			children:
				assets.length > 0 ? (
					<AssetTable
						data={assets}
						onViewAsset={(asset) => setSelectedAsset(asset)}
						onDeleteAssets={(assetIds) =>
							deleteAssets(
								assetIds.map((a) => a.id),
								{
									onSuccess: () => {
										queryClient.invalidateQueries({
											queryKey: ["project", projectId],
										});
										notify.success(DELETE_RESOURCE_INFO_COMPLETED);
									},
									onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
								},
							)
						}
					/>
				) : (
					<CustomEmpty
						description="Assets"
						onAdd={() => setUploadAssetOpen(true)}
					/>
				),
		},
		{
			key: "links",
			label: tabLabelWithIcon(IconLink, "Links", links.length),
			children: (
				<ProjectLinks project={project} onAdd={() => setAddLinkOpen(true)} />
			),
		},
		{
			key: "repositories",
			label: tabLabelWithIcon(
				IconGitBranch,
				"Repositories",
				projectRepositories.length,
			),
			children: (
				<QueryGuard
					isPending={projectRepositoriesPending}
					isError={projectRepositoriesError}
				>
					{!(gitHubAvailable && gitProviderConnection) ? (
						<CustomEmpty description="Repositories" expanded>
							<Flex vertical align="center" gap={8}>
								<Typography.Text type="secondary">
									Connect a GitHub App installation to link repositories to this
									project.
								</Typography.Text>
								<Button
									onClick={() =>
										navigate({
											to: "/$orgSlug/workspaces/$workspaceId/settings",
											params: { orgSlug, workspaceId },
										})
									}
								>
									Go to workspace settings
								</Button>
							</Flex>
						</CustomEmpty>
					) : projectRepositories.length > 0 ? (
						<CardViewer<ProjectRepository>
							items={projectRepositories}
							getKey={(r) => r.id}
							onOpen={(r) => setSelectedRepository(r)}
							renderCard={(r) => (
								<CardBody
									title={r.repositoryFullName}
									extra={<Tag>{toLabel(r.provider)}</Tag>}
									// Hardcoded for now — the only connected provider is GitHub
									// (GitProviderType has one member). Swap for a real field once
									// a second provider ships.
									// description={`GitHub · ${r.defaultBranch ?? "-"}`}
								/>
							)}
						/>
					) : (
						<CustomEmpty
							description="Repositories"
							onAdd={() => setRepositoryBrowserOpen(true)}
						/>
					)}
				</QueryGuard>
			),
		},
		...(gitHubAvailable &&
		gitProviderConnection &&
		projectRepositories.length > 0
			? [
					{
						key: "dependencies",
						label: tabLabelWithIcon(IconSitemap, "Dependencies"),
						children: <ProjectCatalogGraph projectId={projectId} />,
					},
				]
			: []),
		...(project?.type === ProjectType.SOFTWARE_APPLICATION
			? [
					{
						key: "deployments",
						label: tabLabelWithIcon(
							IconRocket,
							"Deployments",
							deployments.length,
						),
						children:
							deployments.length > 0 ? (
								<CardViewer<Deployment>
									items={deployments}
									getKey={(d) => d.id}
									onOpen={(d) => {
										setSelectedDeployment(d);
										setDeploymentViewerOpen(true);
									}}
									renderCard={(d) => (
										<CardBody
											title={d.name}
											extra={
												<EnvironmentClassificationBadge
													classification={
														environments?.find((e) => e?.id === d.environmentId)
															?.classification ??
														EnvironmentClassification.NON_PRODUCTION
													}
												/>
											}
											description={d.description || "No description"}
											footer={
												d.graph?.isDiscovering ? (
													<Typography.Text type="secondary">
														Discovering…
													</Typography.Text>
												) : d.graph?.lastDiscoveryAt ? (
													<Typography.Text type="secondary">
														Discovered{" "}
														{formatDistanceToNow(
															new Date(d.graph.lastDiscoveryAt as string),
															{ addSuffix: true },
														)}
													</Typography.Text>
												) : (
													<Typography.Text type="secondary">
														Not yet discovered
													</Typography.Text>
												)
											}
										/>
									)}
								/>
							) : (
								<CustomEmpty
									description="Deployments"
									onAdd={() => setDeploymentCreateOpen(true)}
								/>
							),
					},
				]
			: []),
	];

	return (
		<QueryGuard isPending={isPending} isError={isError} isNull={!project}>
			<BasePageContainer>
				<Flex vertical gap={16}>
					{deployments.length === 0 && (
						<Alert
							type="info"
							closable
							description="Add a deployment to connect this project to one of your environments."
							action={
								<Button
									size="small"
									type="primary"
									onClick={() => setDeploymentCreateOpen(true)}
								>
									Add deployment
								</Button>
							}
						/>
					)}
					{signals.some(isHealthSignal) && (
						<SignalSummary
							signals={signals}
							statuses={signalStatuses.data}
							isFetching={signalStatuses.isFetching}
							onRefresh={() => signalStatuses.refetch()}
						/>
					)}
					<SignalKeyNumbers
						signals={signals}
						statuses={signalStatuses.data}
						isFetching={signalStatuses.isFetching}
						onOpen={(s) => setSelectedSignalId(s.id)}
					/>
					<BaseContainer title="Details">
						<ProjectDetails
							project={project!}
							onSaveName={(name) => save({ name })}
							onSaveDescription={(description) => save({ description })}
							onSaveStatus={(status) => save({ status })}
							onSaveOwner={(ownerId) => save({ ownerId })}
						/>
					</BaseContainer>

					<BaseContainer>
						<Tabs
							defaultActiveKey={linkedDeploymentId ? "deployments" : "signals"}
							items={tabItems}
						/>
					</BaseContainer>
				</Flex>
			</BasePageContainer>

			<AssetViewerDrawer
				asset={selectedAsset}
				onClose={() => setSelectedAsset(null)}
			/>

			<DocumentEditorDrawer
				title="Instructions"
				content={project?.instructions || ""}
				open={instructionsOpen}
				onSave={(content) => save({ instructions: content })}
				onClose={() => setInstructionsOpen(false)}
			/>

			<AddLinkModal
				open={addLinkOpen}
				onClose={() => setAddLinkOpen(false)}
				onAdd={handleAddLink}
			/>

			<NoteDrawer
				open={addNoteOpen}
				initialValue=""
				onSave={(note) => {
					handleAddNote(note);
					setAddNoteOpen(false);
				}}
				onClose={() => setAddNoteOpen(false)}
			/>

			<UploadAssetDialog
				open={uploadAssetOpen}
				onClose={() => setUploadAssetOpen(false)}
				parentId={projectId}
				parentType={ParentType.PROJECT}
				workspaceId={workspaceId}
				onUploadComplete={() =>
					queryClient.invalidateQueries({ queryKey: ["project", projectId] })
				}
			/>

			<DeploymentViewerDrawer
				open={deploymentViewerOpen}
				onClose={() => setDeploymentViewerOpen(false)}
				onDeleted={() => refetch()}
				deployment={selectedDeployment}
				projectId={projectId}
				workspaceId={workspaceId}
				signals={signals.filter(
					(s) => s.deploymentId === selectedDeployment?.id,
				)}
				signalStatuses={signalStatuses.data}
				signalStatusesFetching={signalStatuses.isFetching}
				onOpenSignal={(s) => setSelectedSignalId(s.id)}
				onAddSignal={(deploymentId) =>
					setSignalCreate({ open: true, deploymentId })
				}
			/>

			<SignalViewerDrawer
				signal={selectedSignal}
				status={
					selectedSignal ? signalStatuses.data?.[selectedSignal.id] : undefined
				}
				deployments={deployments}
				open={!!selectedSignal}
				onClose={() => setSelectedSignalId(null)}
			/>

			<SignalCreateWizard
				open={signalCreate.open}
				onClose={() => setSignalCreate({ open: false })}
				projectId={projectId}
				workspaceId={workspaceId}
				deployments={deployments}
				existingSignals={signals}
				initialDeploymentId={signalCreate.deploymentId}
			/>

			<DeploymentCreateWizard
				open={deploymentCreateOpen}
				onClose={() => setDeploymentCreateOpen(false)}
				onCreated={() => refetch()}
				projectId={projectId}
				workspaceId={workspaceId}
			/>

			{gitHubAvailable && gitProviderConnection && (
				<ProjectRepositoryBrowser
					open={repositoryBrowserOpen}
					onClose={() => setRepositoryBrowserOpen(false)}
					workspaceId={workspaceId}
					projectId={projectId}
					gitProviderConnectionId={gitProviderConnection.id}
					onLinked={() =>
						queryClient.invalidateQueries({
							queryKey: ["projectRepositories", projectId],
						})
					}
				/>
			)}

			<ProjectRepositoryViewerDrawer
				repository={selectedRepository}
				open={!!selectedRepository}
				onClose={() => setSelectedRepository(null)}
				onUnlink={() => {
					if (!selectedRepository) return;
					confirmDestructive({
						title: "Unlink repository",
						content: `Unlink ${selectedRepository.repositoryFullName} from this project?`,
						onOk: () =>
							unlinkProjectRepository.mutate(selectedRepository.id, {
								onSuccess: () => {
									notify.success(DELETE_RESOURCE_INFO_COMPLETED);
									setSelectedRepository(null);
								},
								onError: (err) =>
									notify.error(
										(err as Error).message || DELETE_RESOURCE_ERROR_FAILED,
									),
							}),
					});
				}}
			/>
			{dialog}
		</QueryGuard>
	);
}
